/**
 * このファイルの目的
 * 組織が保持する全ての情報について、DBへのアクセスを隠蔽して、UIロジック層が使いやすい形で保持・提供する
 *
 *
 * やるべきこと
 * UIロジックに対して、オブジェクトの一覧を返したり、指定されたオブジェクトを返したり、書き込み（更新）要求を受け付けてDBに登録する機能を提供する
 * DB(cacheManage)からorganizationTableのデータを取得して、ターゲットの情報だけを分離して、一つ一つのオブジェクトにする
 * 個別のオブジェクトが更新するときに、必要最小限のDBレコードを更新登録する
 */

// 消しといてください
import { bulkPutInOrganizationTable, delInOrganizationTable, getAllOrganizationTableData, putInOrganizationTable } from './dataAccess';
import { IOrganizationRecordHeader } from '@/coreDomainsModule/entities/record';

/**
 *
 */
class OrganizationDataManage<T> {
  protected allEntries: { [key: string]: T } = {};
  protected allDataIds: { [key: string]: string[] } = {};
  protected idName = '';
  protected arranger: (ent: T) => (T | IOrganizationRecordHeader)[] | (T | IOrganizationRecordHeader);

  /**
   *
   * @param idName
   * @param arranger
   */
  constructor(idName: string, arranger?: (ent: T) => (T | IOrganizationRecordHeader)[] | (T | IOrganizationRecordHeader)) {
    this.idName = idName;
    if (arranger) {
      this.arranger = arranger;
    } else {
      this.arranger = (ent) => {
        return { organizationId: '', dataId: '', ...ent };
      };
    }
  }

  /**
   * DBとallEntriesを同期する(DBから取得した情報でallEntriesを更新する)
   * @param entry
   */
  public sync(entry: T & IOrganizationRecordHeader) {
    if (!entry[this.idName]) return;
    if (!this.allDataIds[entry[this.idName]]) this.allDataIds[entry[this.idName]] = [];
    if (!this.allDataIds[entry[this.idName]].includes(entry.dataId)) {
      this.allDataIds[entry[this.idName]].push(entry.dataId);
    }

    if (entry.deleted && this.allEntries[entry[this.idName]]) {
      delete this.allEntries[entry[this.idName]];
    } else {
      Object.keys(entry).forEach((ent) => {
        if (['organizationId', 'dataId', 'updatedAt', 'deleted', 'removeAt', 'dataType'].includes(ent)) {
          delete entry[ent];
        }
      });
      this.allEntries[entry[this.idName]] = { ...this.allEntries[entry[this.idName]], ...entry };
    }
  }

  /**
   * 指定したbusinessIdの取引オブジェクト群を返す
   * @param ids {string[]}
   */
  public async getById(ids: string[]): Promise<any[]> {
    await getAllOrganizationTableData(); // 最新情報を取得する。このメソッドの中で、syncBusinessEntries()が実行されるのでallEntriesが最新になる
    const res: T[] = [];
    ids.forEach((p) => {
      res.push(this.allEntries[p]);
    });
    return res;
  }

  /**
   * 全ての情報を返す
   */
  public async getAll(): Promise<T[]> {
    await getAllOrganizationTableData(); // 最新情報を取得する。このメソッドの中で、sync()が実行されるのでallEntriesが最新になる
    return Object.values(this.allEntries);
  }

  /**
   * 情報を登録・更新する
   * @param entry
   */
  public async register(entry: any): Promise<boolean> {
    // DBに登録する
    const entries = this.arranger(entry);
    let res: any;
    if (Array.isArray(entries)) {
      res = bulkPutInOrganizationTable(entries as any[]);
    } else {
      res = putInOrganizationTable(entries as any);
    }

    // allEntriesを更新する
    if (res) {
      this.sync(entry);
      return true;
    } else {
      return false;
    }
  }

  /**
   * 複数の商品をDBに登録する（新規or更新）
   * @param entries
   */
  public async bulkRegister(entries: T[]): Promise<boolean> {
    // DBに登録する
    let toRegister: any[] = [];
    for (const entry of entries) {
      const entriesToRegister = this.arranger(entry as any);
      // console.log('ent', ent);
      if (Array.isArray(entriesToRegister)) {
        toRegister = [...toRegister, ...entriesToRegister];
      } else {
        toRegister = [...toRegister, entriesToRegister];
      }
    }
    await bulkPutInOrganizationTable(toRegister as any);

    // allEntriesを更新する
    entries.forEach((ent: any) => {
      this.sync(ent);
    });

    return true;
  }

  /**
   * オブジェクトを削除する
   * @param ids {string[]} 削除したい取引オブジェクトのId群
   */
  public async remove(ids: string[]) {
    let dataIdsToRemove: string[] = [];
    ids.forEach((p) => {
      if (this.allDataIds[p]) dataIdsToRemove = [...dataIdsToRemove, ...this.allDataIds[p]];
    });
    await delInOrganizationTable(dataIdsToRemove);

    for (const i of ids) {
      delete this.allEntries[i];
      delete this.allDataIds[i];
    }
  }
}

export { OrganizationDataManage };
